import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  AvailableActivity,
  useAvailableActivities,
  useSendActivity,
} from "@/lib/api/activities";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePostHogEvent } from "@/lib/posthog";
import { FireIcon } from "@heroicons/react/24/outline";
import { Gamepad, MonitorPlay } from "lucide-react";
import { useFeatureFlagEnabled } from "@posthog/react";
import {
  HotTakesComposer,
  type HotTakesMode,
  HotTakesModePicker,
} from "./hot-takes";
import { ConnectMomentSetupDialog } from "./connect-moment/connect-moment-setup-dialog";

interface ActivityTriggerProps {
  mentorshipId: string;
  teenName: string;
}

interface ActivityItem extends AvailableActivity {
  icon: ReactNode;
}

export function ActivityTrigger({
  mentorshipId,
  teenName,
}: ActivityTriggerProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [confirmActivity, setConfirmActivity] =
    useState<AvailableActivity | null>(null);
  const [showSameDayWarning, setShowSameDayWarning] = useState(false);

  // Hot Takes composer state
  const [hotTakesPickerOpen, setHotTakesPickerOpen] = useState(false);
  const [hotTakesMode, setHotTakesMode] = useState<HotTakesMode | null>(null);

  const [connectSetupOpen, setConnectSetupOpen] = useState(false);

  const { data: availability } = useAvailableActivities(mentorshipId);
  const sendMutation = useSendActivity();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");
  const functionalMomentsEnabled =
    useFeatureFlagEnabled("functional-moments") ||
    import.meta.env.VITE_STYTCH_PUBLIC_ENV === "development";
  const hotTakesFlagEnabled = useFeatureFlagEnabled("activity_hot_takes");
  // Flag OFF → don't hide; gray out with "Coming soon" (anticipation builder).
  // Flag ON → preserve the backend's enabled/reason so weekly-cap/first-message
  //            gating still work.
  const activities = useMemo<ActivityItem[]>(() => {
    const list = availability?.activities ?? [];
    return list.map((a) => {
      const activity = {
        ...a,
        icon: <MonitorPlay className="h-5 w-5 shrink-0 text-zinc-600" />,
      } satisfies ActivityItem;

      if (activity.type === "hot_takes") {
        activity.enabled = !!hotTakesFlagEnabled;
        activity.reason = hotTakesFlagEnabled ? null : "coming_soon";
        activity.icon = <FireIcon className="h-5 w-5 shrink-0 text-zinc-600" />;
      }

      return activity;
    });
  }, [availability, hotTakesFlagEnabled]);

  const handleTriggerClick = useCallback(() => {
    dispatchEvent("activity.mentor.tab_opened", { mentorshipId });
  }, [dispatchEvent, mentorshipId]);

  const handleSelectActivity = useCallback(
    (activity: AvailableActivity) => {
      dispatchEvent("activity.mentor.type_selected", {
        mentorshipId,
        activityType: activity.type,
      });

      if (!activity.enabled) {
        dispatchEvent("activity.mentor.blocked", {
          mentorshipId,
          reason: activity.reason,
        });
        return;
      }

      // Hot Takes has its own composer flow — skip the confirm dialog.
      if (activity.type === "hot_takes") {
        setPopoverOpen(false);
        setHotTakesPickerOpen(true);
        dispatchEvent("activity.mentor.compose_opened", { mentorshipId });
        return;
      }

      if (activity.type === "connect_moment") {
        setPopoverOpen(false);
        setConnectSetupOpen(true);
        dispatchEvent("activity.mentor.compose_opened", { mentorshipId });
        return;
      }

      if (availability?.sentToday) {
        dispatchEvent("activity.mentor.same_day_warning_shown", {
          mentorshipId,
        });
        setShowSameDayWarning(true);
      }

      setConfirmActivity(activity);
      setPopoverOpen(false);
    },
    [availability, mentorshipId, dispatchEvent],
  );

  const handleConfirmSend = useCallback(() => {
    if (!confirmActivity) return;

    if (showSameDayWarning) {
      dispatchEvent("activity.mentor.same_day_warning_confirmed", {
        mentorshipId,
        confirmed: true,
      });
    }

    sendMutation.mutate(
      {
        mentorshipId,
        activityType: confirmActivity.type,
      },
      {
        onSuccess: () => {
          dispatchEvent("activity.mentor.sent", {
            mentorshipId,
            activityType: confirmActivity!.type,
          });
          setConfirmActivity(null);
          setShowSameDayWarning(false);
        },
        onError: () => {
          setConfirmActivity(null);
          setShowSameDayWarning(false);
        },
      },
    );
  }, [
    confirmActivity,
    showSameDayWarning,
    mentorshipId,
    sendMutation,
    dispatchEvent,
  ]);

  const handleCancelSend = useCallback(() => {
    if (showSameDayWarning) {
      dispatchEvent("activity.mentor.same_day_warning_confirmed", {
        mentorshipId,
        confirmed: false,
      });
    }
    setConfirmActivity(null);
    setShowSameDayWarning(false);
  }, [showSameDayWarning, mentorshipId, dispatchEvent]);

  const handleModeSelected = useCallback(
    (mode: HotTakesMode) => {
      dispatchEvent("activity.mentor.compose_mode_selected", {
        mentorshipId,
        mode,
      });
      setHotTakesMode(mode);
      setHotTakesPickerOpen(false);
    },
    [dispatchEvent, mentorshipId],
  );

  const handleComposerClose = useCallback(() => {
    setHotTakesMode(null);
  }, []);

  const getTooltipText = (activity: AvailableActivity) => {
    if (!activity.enabled) {
      switch (activity.reason) {
        case "weekly_cap_reached":
          return "Nice momentum - you've hit this week's activity limit. Try again next week.";
        case "no_first_message":
          return "This unlocks once your teen sends their first message.";
        case "feature_flag_disabled":
        case "coming_soon":
          return "We're putting the finishing touches on this. Coming soon.";
        default:
          return "This activity isn't available right now. Check back soon.";
      }
    }

    if (!activity.isTeenEligible)
      return "This activity unlocks once your teen updates to the latest app version.";

    return "";
  };

  if (!functionalMomentsEnabled) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="ml-2 gap-2"
            onClick={handleTriggerClick}
            title="Send an activity"
          >
            <Gamepad className="h-4 w-4" />
            Engagement
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-52 rounded-2xl border-[#dfdad3] p-2 shadow-xl"
          align="end"
          side="bottom"
        >
          <div className="flex flex-col gap-1">
            {activities.map((activity) => {
              const tooltipText = getTooltipText(activity);

              const row = (
                <button
                  key={activity.type}
                  onClick={() => handleSelectActivity(activity)}
                  disabled={!activity.enabled || !activity.isTeenEligible}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left text-base text-[#1f1e1d] transition-colors hover:bg-[#f7f5f1] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {activity.icon}
                  <span className="truncate">{activity.label}</span>
                </button>
              );

              if (tooltipText) {
                return (
                  <Tooltip key={activity.type}>
                    <TooltipTrigger asChild>
                      <div>{row}</div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      {tooltipText}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return row;
            })}
            {!activities.length && (
              <p className="py-2 text-center text-sm text-zinc-400">
                No activities right now - check back soon.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ConfirmActionDialog
        isOpen={!!confirmActivity}
        onClose={handleCancelSend}
        onConfirm={handleConfirmSend}
        title={
          showSameDayWarning
            ? "Activity already sent today"
            : `Send ${confirmActivity?.label}?`
        }
        description={
          showSameDayWarning
            ? `You've already sent an activity to ${teenName} today. Send another?`
            : `This will send a ${confirmActivity?.label} to ${teenName}.`
        }
        loading={sendMutation.isPending}
        confirmText="Send"
        confirmVariant="default"
      />

      <HotTakesModePicker
        isOpen={hotTakesPickerOpen}
        onClose={() => setHotTakesPickerOpen(false)}
        onSelectMode={handleModeSelected}
      />

      {hotTakesMode && (
        <HotTakesComposer
          isOpen
          mode={hotTakesMode}
          mentorshipId={mentorshipId}
          teenName={teenName}
          onClose={handleComposerClose}
          onBack={() => {
            setHotTakesMode(null);
            setHotTakesPickerOpen(true);
          }}
          onSent={() => {
            dispatchEvent("activity.mentor.sent", {
              mentorshipId,
              activityType: "hot_takes",
            });
          }}
        />
      )}

      <ConnectMomentSetupDialog
        open={connectSetupOpen}
        onOpenChange={setConnectSetupOpen}
        mentorshipId={mentorshipId}
        teenName={teenName}
      />
    </TooltipProvider>
  );
}
